import { MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { formatAddress, toTitleCase } from "@utils/utils";
import clsx from "clsx";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Breadcrumb, BreadcrumbLink, Button, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ClinicListingType, useClinicListingsQuery } from "@api/graphql/types-and-hooks";
import { CreateClinicListingOutput } from "@api/rest/types-and-hooks";
import { AddEditClinicForm } from "@pages/Clinic/AddEditClinicForm";
import { InternalVerificationPending } from "@pages/Clinic/InternalVerificationPending";
import { SelectedClinic } from "@pages/Clinic/SelectedClinic";

export const AddClinic: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedClinic, setSelectedClinic] = useState<ClinicListingType | null>(null);
  const [showAddClinicForm, setShowAddClinicForm] = useState<boolean>(false);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [pendingClinic, setPendingClinc] = useState<CreateClinicListingOutput>();

  const { data, isLoading } = useClinicListingsQuery(
    {
      filter: {
        searchTerm: debouncedSearchTerm,
        isClaimed: false,
      },
    },
    { queryKey: ["clinicListings", debouncedSearchTerm], enabled: !!searchTerm },
  );

  const breadcrumbLinks = [
    {
      type: "component",
      name: "Clinics",
      current: false,
      Component: (
        <Link to="/clinics" className="text-text-secondary">
          Clinics
        </Link>
      ),
    },
    {
      type: "component",
      name: "Add Clinic",
      current: true,
      Component: <span className="text-text-secondary">Add Clinic</span>,
    },
  ] as BreadcrumbLink[];

  return (
    <>
      {pendingClinic ? (
        <InternalVerificationPending clinicData={pendingClinic} />
      ) : (
        <div className="flex flex-col items-center mt-8">
          <div className="flex flex-col gap-1 min-w-[70vw]">
            <Breadcrumb
              links={breadcrumbLinks}
              homeLink={{
                name: "Home",
                Component: (
                  <Link className="text-text-secondary" to={`/`}>
                    Home
                  </Link>
                ),
                current: false,
                type: "component",
              }}
            />

            <Headline>Add Clinic</Headline>
            <Paragraph size="small" colorType="secondary">
              Search for your clinic in our directory. If it's not listed, you can add it.
            </Paragraph>

            <div className={clsx("flex flex-col", !showAddClinicForm && "mt-6 p-8 rounded-xl border bg-white")}>
              {selectedClinic ? (
                <SelectedClinic clinic={selectedClinic} setSelectedClinic={setSelectedClinic} />
              ) : showAddClinicForm ? (
                <AddEditClinicForm setShowAddClinicForm={setShowAddClinicForm} setPendingClinc={setPendingClinc} />
              ) : (
                <div className="flex flex-col gap-1 mb-8">
                  <Paragraph size="small">Search for a clinic</Paragraph>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                      <MagnifyingGlassIcon className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search clinic by name, city or zip"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-limestone-200 rounded-md bg-white h-10 focus:outline-hidden focus:ring-0 focus:border-limestone-200"
                    />
                  </div>

                  {/* Skeleton Loading */}
                  {searchTerm && (isLoading || searchTerm !== debouncedSearchTerm) && (
                    <div className="flex flex-col gap-2 mt-6">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-[72px] w-full rounded-xl bg-gray-200 animate-pulse" />
                      ))}
                    </div>
                  )}

                  {/* No Results */}
                  {searchTerm &&
                    !isLoading &&
                    searchTerm === debouncedSearchTerm &&
                    data?.clinicListings?.totalCount === 0 && (
                      <div className="flex flex-col items-center items-center gap-2 mt-6">
                        <Paragraph colorType="secondary" displayType="loud">
                          No clinics found
                        </Paragraph>
                        <Paragraph size="small" colorType="secondary">
                          We couldnt find any clinics matching “{searchTerm}”
                        </Paragraph>
                        {/* TODO: */}
                        <Button text="Manually add clinic" onClick={() => setShowAddClinicForm(true)} />
                      </div>
                    )}

                  {/* Results */}
                  {searchTerm &&
                    !isLoading &&
                    searchTerm === debouncedSearchTerm &&
                    !!(data?.clinicListings?.totalCount && data.clinicListings.totalCount > 0) && (
                      <div className="flex flex-col gap-2 mt-6">
                        <Paragraph size="small" colorType="secondary">
                          Found {data.clinicListings.totalCount} clinics
                        </Paragraph>

                        <div className="flex flex-col gap-2 max-h-[35vh] overflow-y-auto no-scrollbar">
                          {data.clinicListings.edges.map((edge) => {
                            const clinicListing = edge?.node;

                            return (
                              <div
                                key={clinicListing?.id}
                                className="flex flex-row justify-between p-4 border rounded-xl"
                              >
                                <div className="flex flex-col">
                                  <Paragraph colorType="primary" displayType="loud">
                                    {clinicListing?.name}
                                  </Paragraph>
                                  <div className="flex flex-row gap-2 items-center">
                                    <MapPinIcon className="size-6 text-text-secondary" />
                                    <Paragraph colorType="secondary">
                                      {formatAddress({
                                        addressLine1: toTitleCase(clinicListing?.location?.addressLine1) || undefined,
                                        addressLine2: toTitleCase(clinicListing?.location?.addressLine2) || undefined,
                                        city: toTitleCase(clinicListing?.location?.city),
                                        state: clinicListing?.location?.state,
                                        zip: clinicListing?.location?.postalCode,
                                      })}
                                    </Paragraph>
                                  </div>
                                </div>
                                <Button
                                  text="Select"
                                  appearance="secondary"
                                  onClick={() => setSelectedClinic(clinicListing as ClinicListingType)}
                                />
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex flex-row justify-between bg-limestone-50 p-4 border border-dashed rounded-xl">
                          <div className="flex flex-col">
                            <Paragraph colorType="primary" displayType="loud">
                              Can't find your clinic?
                            </Paragraph>
                            <Paragraph colorType="secondary">Add a new clinic to our directory</Paragraph>
                          </div>
                          <Button text="Add New" appearance="primary" onClick={() => setShowAddClinicForm(true)} />
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
